import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/types/job';
import { BidLineItem } from '@/hooks/useBidLineItems';
import { useJobAward } from '@/hooks/useJobAward';
import { toast } from 'sonner';
import { RotateCcw, AlertTriangle, CheckCircle, XCircle, Loader2, ArrowRight, ArrowLeft } from 'lucide-react';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { SELECTION_REASONS, FEEDBACK_COMMENTS_MAX_LENGTH } from '@/lib/constants/awardFeedback';
/**
 * Matrix entry containing both quantity and percentage for a location
 */
interface DistributionMatrixEntry {
  quantity?: number;
  percentage?: number;
  notes?: string;
  id?: string;
}

/**
 * Represents the awarded quantities and pricing for a single specification
 */
export interface AwardSelection {
  specification_id: string;
  quantity_option: number;
  bid_line_item_id: string;
  unit_price_cents: number;
  total_price_cents: number;
}

/**
 * Distribution quantities for each specification across delivery locations
 */
export interface DistributionQuantities {
  [specId: string]: {
    [locationId: string]: number;
  };
}

/**
 * Props for the AwardSelectionModal component
 */
interface AwardSelectionModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback to close the modal */
  onOpenChange: (open: boolean) => void;
  /** The job being awarded */
  jobId: string;
  /** The bid that won the award */
  winningBid: any;
  /** All line items from the winning bid */
  lineItems: BidLineItem[];
  /** Product specifications for the job */
  specifications: Array<{ id: string; specification_id: string; name: string }>;
  /** Quantity options that were available in the RFQ */
  quantityOptions: Array<{ id: string; specification_id: string; quantity_option: number; unit: string; notes?: string }>;
  /** Delivery locations for distribution calculations */
  deliveryLocations: Array<{ id: string; delivery_name: string }>;
  /** Matrix of distribution data (quantity and percentage) across locations */
  distributionMatrix: Record<string, Record<string, DistributionMatrixEntry>>;
}

/**
 * Modal for selecting specific quantities when awarding a bid
 * 
 * This component handles the award selection workflow for jobs with multiple quantity options:
 * - For each specification, the client selects which quantity option to award
 * - Displays pricing and distribution information for each option
 * - Allows editing of distribution quantities before finalizing
 * - Calculates the total award amount based on selections
 * - Prevents award until all specifications have quantity selections
 * - Validates distribution totals match awarded quantities
 */
export const AwardSelectionModal = ({
  open,
  onOpenChange,
  jobId,
  winningBid,
  lineItems,
  specifications,
  quantityOptions,
  deliveryLocations,
  distributionMatrix
}: AwardSelectionModalProps) => {
  const { awardJob, loading } = useJobAward();
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [awardNotes, setAwardNotes] = useState('');
  
  // Step management for 2-step flow
  type ModalStep = 'quantities' | 'feedback';
  const [currentStep, setCurrentStep] = useState<ModalStep>('quantities');
  
  // Feedback state
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [feedbackComments, setFeedbackComments] = useState('');
  
  // Track which specifications are excluded from the award
  const [excludedSpecs, setExcludedSpecs] = useState<Set<string>>(new Set());
  
  // Editable distribution quantities: { specId: { locationId: quantity } }
  const [distributionQuantities, setDistributionQuantities] = useState<DistributionQuantities>({});
  
  // String-based input values to allow empty fields during editing
  const [inputValues, setInputValues] = useState<Record<string, Record<string, string>>>({});
  
  // Recalculated tax state for when distribution changes
  const [recalculatedTax, setRecalculatedTax] = useState<{
    totalTaxCents: number;
    taxBySpec: Record<string, number>;
    calculations?: Array<{
      specificationId: string;
      locationId: string;
      quantity: number;
      taxableAmountCents: number;
      taxCents: number;
      taxRate: number;
    }>;
  } | null>(null);
  const [taxLoading, setTaxLoading] = useState(false);
  const taxRecalcTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setCurrentStep('quantities');
      setSelectedReasons([]);
      setFeedbackComments('');
    }
  }, [open]);

  // Compute included specifications
  const includedSpecs = useMemo(() => 
    specifications.filter(s => !excludedSpecs.has(s.specification_id)),
    [specifications, excludedSpecs]
  );

  // Handle toggling a specification's inclusion in the award
  const handleSpecInclusionToggle = useCallback((specId: string, included: boolean) => {
    setExcludedSpecs(prev => {
      const newSet = new Set(prev);
      if (included) {
        newSet.delete(specId);
      } else {
        newSet.add(specId);
        // Clear selection and distribution when excluding
        setSelections(s => {
          const copy = { ...s };
          delete copy[specId];
          return copy;
        });
        setDistributionQuantities(d => {
          const copy = { ...d };
          delete copy[specId];
          return copy;
        });
        setInputValues(v => {
          const copy = { ...v };
          delete copy[specId];
          return copy;
        });
      }
      return newSet;
    });
  }, []);

  // Check if we need distribution editing (multiple locations and percentage-based distribution)
  const needsDistributionConfirmation = useMemo(() => {
    return deliveryLocations.length > 1;
  }, [deliveryLocations.length]);

  // Group line items by specification
  const itemsBySpec = lineItems.reduce((acc, item) => {
    if (!acc[item.specification_id]) {
      acc[item.specification_id] = [];
    }
    acc[item.specification_id].push(item);
    return acc;
  }, {} as Record<string, BidLineItem[]>);

  // Calculate initial distribution for a specification based on existing data or percentages
  const calculateInitialDistribution = useCallback((specId: string, selectedQuantity: number) => {
    const newDistribution: Record<string, number> = {};
    const newInputValues: Record<string, string> = {};
    
    deliveryLocations.forEach(loc => {
      const matrixEntry = distributionMatrix[specId]?.[loc.id];
      const existingQty = matrixEntry?.quantity || 0;
      const percentage = matrixEntry?.percentage || 0;
      
      // Prioritize existing quantity if it exists and is non-zero
      // Otherwise calculate from percentage
      let qty: number;
      if (existingQty > 0) {
        // Scale proportionally if the selected quantity differs from the sum of existing quantities
        const totalExisting = deliveryLocations.reduce((sum, l) => 
          sum + (distributionMatrix[specId]?.[l.id]?.quantity || 0), 0);
        if (totalExisting > 0 && totalExisting !== selectedQuantity) {
          qty = Math.round((existingQty / totalExisting) * selectedQuantity);
        } else {
          qty = existingQty;
        }
      } else {
        qty = Math.round((selectedQuantity * percentage) / 100);
      }
      
      newDistribution[loc.id] = qty;
      newInputValues[loc.id] = qty.toString();
    });
    
    return { distribution: newDistribution, inputValues: newInputValues };
  }, [deliveryLocations, distributionMatrix]);

  const handleSelectionChange = (specId: string, lineItemId: string) => {
    setSelections(prev => ({ ...prev, [specId]: lineItemId }));
    
    // When a quantity option is selected, calculate and set initial distribution quantities
    if (needsDistributionConfirmation) {
      const lineItem = lineItems.find(li => li.id === lineItemId);
      if (lineItem) {
        const selectedQuantity = lineItem.quantity_option;
        const { distribution, inputValues: newInputVals } = calculateInitialDistribution(specId, selectedQuantity);
        
        setDistributionQuantities(prev => ({
          ...prev,
          [specId]: distribution
        }));
        
        setInputValues(prev => ({
          ...prev,
          [specId]: newInputVals
        }));
      }
    }
  };

  const handleDistributionChange = (specId: string, locationId: string, quantity: number) => {
    setDistributionQuantities(prev => ({
      ...prev,
      [specId]: {
        ...prev[specId],
        [locationId]: quantity
      }
    }));
  };

  const handleInputChange = (specId: string, locationId: string, value: string) => {
    // Only allow numeric input
    const numericValue = value.replace(/[^0-9]/g, '');
    setInputValues(prev => ({
      ...prev,
      [specId]: {
        ...prev[specId],
        [locationId]: numericValue
      }
    }));
  };

  const handleInputBlur = (specId: string, locationId: string) => {
    const value = inputValues[specId]?.[locationId] || '';
    const numValue = parseInt(value) || 0;
    handleDistributionChange(specId, locationId, numValue);
    // Normalize the display value
    setInputValues(prev => ({
      ...prev,
      [specId]: {
        ...prev[specId],
        [locationId]: numValue.toString()
      }
    }));
  };

  const resetDistributionToCalculated = (specId: string) => {
    const lineItemId = selections[specId];
    if (!lineItemId) return;
    
    const lineItem = lineItems.find(li => li.id === lineItemId);
    if (!lineItem) return;
    
    const selectedQuantity = lineItem.quantity_option;
    const newDistribution: Record<string, number> = {};
    const newInputValues: Record<string, string> = {};
    
    deliveryLocations.forEach(loc => {
      const percentage = distributionMatrix[specId]?.[loc.id]?.percentage || 0;
      const qty = Math.round((selectedQuantity * percentage) / 100);
      newDistribution[loc.id] = qty;
      newInputValues[loc.id] = qty.toString();
    });
    
    setDistributionQuantities(prev => ({
      ...prev,
      [specId]: newDistribution
    }));
    
    setInputValues(prev => ({
      ...prev,
      [specId]: newInputValues
    }));
  };

  const isComplete = () => {
    // Must have at least one included spec and all included specs must have selections
    if (includedSpecs.length === 0) return false;
    return includedSpecs.every(spec => selections[spec.specification_id]);
  };

  // Calculate distribution total for a specification
  const getDistributionTotal = (specId: string): number => {
    const distribution = distributionQuantities[specId];
    if (!distribution) return 0;
    return Object.values(distribution).reduce((sum, qty) => sum + (qty || 0), 0);
  };

  // Get selected quantity for a specification
  const getSelectedQuantityValue = (specId: string): number => {
    const lineItemId = selections[specId];
    if (!lineItemId) return 0;
    const lineItem = lineItems.find(li => li.id === lineItemId);
    return lineItem?.quantity_option || 0;
  };

  // Check if distribution is valid for a specification
  const isDistributionValid = (specId: string): boolean => {
    if (!needsDistributionConfirmation) return true;
    if (!selections[specId]) return true; // Not yet selected, no validation needed
    
    const total = getDistributionTotal(specId);
    const expected = getSelectedQuantityValue(specId);
    return total === expected;
  };

  // Check if all distributions are valid (only for included specs)
  const allDistributionsValid = useMemo(() => {
    if (!needsDistributionConfirmation) return true;
    return includedSpecs.every(spec => isDistributionValid(spec.specification_id));
  }, [includedSpecs, distributionQuantities, selections, needsDistributionConfirmation]);

  // Track if distribution has been modified from original
  const distributionModified = useMemo(() => {
    if (!needsDistributionConfirmation) return false;
    
    // Check if any distribution quantity differs from what was originally calculated
    for (const spec of includedSpecs) {
      const specId = spec.specification_id;
      const lineItemId = selections[specId];
      if (!lineItemId) continue;
      
      const lineItem = lineItems.find(li => li.id === lineItemId);
      if (!lineItem) continue;
      
      const selectedQuantity = lineItem.quantity_option;
      const currentDist = distributionQuantities[specId];
      if (!currentDist) continue;
      
      // Compare with original percentage-based distribution
      for (const loc of deliveryLocations) {
        const percentage = distributionMatrix[specId]?.[loc.id]?.percentage || 0;
        const originalQty = Math.round((selectedQuantity * percentage) / 100);
        const currentQty = currentDist[loc.id] || 0;
        
        if (originalQty !== currentQty) {
          return true;
        }
      }
    }
    return false;
  }, [includedSpecs, selections, distributionQuantities, distributionMatrix, deliveryLocations, lineItems, needsDistributionConfirmation]);

  // Recalculate tax when distribution changes
  useEffect(() => {
    // Only recalculate if distributions were modified and are valid
    if (!needsDistributionConfirmation || !distributionModified || !allDistributionsValid) {
      if (!distributionModified) {
        setRecalculatedTax(null); // Reset to original tax if distribution reset
      }
      return;
    }

    // Clear any pending timeout
    if (taxRecalcTimeoutRef.current) {
      clearTimeout(taxRecalcTimeoutRef.current);
    }

    // Debounce the recalculation
    taxRecalcTimeoutRef.current = setTimeout(async () => {
      setTaxLoading(true);
      
      try {
        // Build line items with selected unit prices and origin locations
        const taxLineItems = includedSpecs
          .filter(spec => selections[spec.specification_id])
          .map(spec => {
            const lineItemId = selections[spec.specification_id];
            const item = lineItems.find(li => li.id === lineItemId);
            return {
              specificationId: spec.specification_id,
              unitPriceCents: item?.unit_price_cents || 0,
              originLocationId: item?.origin_location_id,
            };
          });

        // Only include distributions for included specs
        const filteredDistribution = Object.fromEntries(
          Object.entries(distributionQuantities)
            .filter(([specId]) => !excludedSpecs.has(specId))
        );

        const { data, error } = await invokeEdgeFunction('recalculate-award-tax', {
          body: {
            winningBidId: winningBid?.id,
            jobId,
            distributionQuantities: filteredDistribution,
            lineItems: taxLineItems,
          },
          callerOperation: 'AwardSelectionModal.recalculateTax',
        });

        if (error) {
          console.error('[AwardSelectionModal] Tax recalculation error:', error);
          // Keep showing original tax on error
          return;
        }

        const result = data as { success?: boolean; enabled?: boolean; totalTaxCents?: number; taxBySpec?: Record<string, number>; calculations?: Array<{ specificationId: string; locationId: string; quantity: number; taxableAmountCents: number; taxCents: number; taxRate: number; }> };
        if (result?.success && result?.enabled) {
          setRecalculatedTax({
            totalTaxCents: result.totalTaxCents || 0,
            taxBySpec: result.taxBySpec || {},
            calculations: result.calculations || [],
          });
        }
      } catch (err) {
        console.error('[AwardSelectionModal] Tax recalculation failed:', err);
      } finally {
        setTaxLoading(false);
      }
    }, 600);

    return () => {
      if (taxRecalcTimeoutRef.current) {
        clearTimeout(taxRecalcTimeoutRef.current);
      }
    };
  }, [distributionQuantities, selections, includedSpecs, excludedSpecs, distributionModified, allDistributionsValid, needsDistributionConfirmation, jobId, winningBid?.id, lineItems]);

  const calculateTotal = () => {
    return Object.entries(selections)
      .filter(([specId]) => !excludedSpecs.has(specId))
      .reduce((sum, [_, lineItemId]) => {
        const item = lineItems.find(li => li.id === lineItemId);
        return sum + (item?.total_price_cents || 0);
      }, 0);
  };

  const calculateTotalTax = () => {
    // Use recalculated tax if distribution was modified
    if (recalculatedTax) {
      return recalculatedTax.totalTaxCents;
    }
    
    // Fall back to original bid line item tax
    return Object.entries(selections)
      .filter(([specId]) => !excludedSpecs.has(specId))
      .reduce((sum, [_, lineItemId]) => {
        const item = lineItems.find(li => li.id === lineItemId);
        return sum + (item?.tax_cents || 0);
      }, 0);
  };

  const getSelectedQuantity = (specId: string) => {
    const lineItemId = selections[specId];
    if (!lineItemId) return null;
    
    const lineItem = lineItems.find(li => li.id === lineItemId);
    if (!lineItem) return null;

    const qtyOption = quantityOptions.find(qo => 
      qo.specification_id === specId && qo.quantity_option === lineItem.quantity_option
    );
    return qtyOption;
  };

  const handleAward = async () => {
    if (includedSpecs.length === 0) {
      toast.error('You must include at least one specification in the award');
      return;
    }

    if (!isComplete()) {
      toast.error('Please select a quantity option for all included specifications');
      return;
    }

    if (needsDistributionConfirmation && !allDistributionsValid) {
      toast.error('Distribution quantities must match awarded quantities for all specifications');
      return;
    }

    try {
      // Build selections array only for included specs
      const awardSelections: AwardSelection[] = includedSpecs.map(spec => {
        const lineItemId = selections[spec.specification_id];
        const lineItem = lineItems.find(li => li.id === lineItemId)!;

        return {
          specification_id: spec.specification_id,
          quantity_option: lineItem.quantity_option,
          bid_line_item_id: lineItem.id,
          unit_price_cents: lineItem.unit_price_cents,
          total_price_cents: lineItem.total_price_cents
        };
      });

      // Pass distribution quantities only for included specs
      const finalDistribution = needsDistributionConfirmation 
        ? Object.fromEntries(
            Object.entries(distributionQuantities)
              .filter(([specId]) => !excludedSpecs.has(specId))
          )
        : undefined;

      // Pass recalculated tax data if distribution was modified (including full calculations for per-location breakdown)
      const recalculatedTaxData = recalculatedTax ? {
        totalTaxCents: recalculatedTax.totalTaxCents,
        taxBySpec: recalculatedTax.taxBySpec,
        calculations: recalculatedTax.calculations,
      } : undefined;

      // Build feedback data if any reasons were selected
      const feedbackData = selectedReasons.length > 0
        ? {
            selectionReasons: selectedReasons,
            additionalComments: feedbackComments.trim() || undefined,
          }
        : undefined;

      await awardJob(jobId, winningBid.id, awardSelections, awardNotes, finalDistribution, recalculatedTaxData, feedbackData);
      onOpenChange(false);
    } catch (err) {
      // Error already shown by useJobAward
      console.error('Award failed:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {currentStep === 'quantities' 
              ? 'Select Quantities to Award' 
              : 'Selection Feedback'}
          </DialogTitle>
          <DialogDescription>
            {currentStep === 'quantities' ? (
              <>
                Choose one quantity option for each specification from the winning bid
                {needsDistributionConfirmation && ' and confirm the distribution across delivery locations'}
              </>
            ) : (
              'Help vendors understand what influenced your decision'
            )}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Quantity Selection */}
        {currentStep === 'quantities' && (
        <div className="space-y-6">
          {specifications.map(spec => {
            const items = itemsBySpec[spec.specification_id] || [];
            const selectedQty = getSelectedQuantity(spec.specification_id);
            const distributionTotal = getDistributionTotal(spec.specification_id);
            const expectedTotal = getSelectedQuantityValue(spec.specification_id);
            const isValid = isDistributionValid(spec.specification_id);

            const isExcluded = excludedSpecs.has(spec.specification_id);

            return (
              <Card 
                key={spec.id} 
                className={isExcluded ? 'opacity-60 border-dashed' : ''}
              >
                <CardHeader>
                  <CardTitle className="text-base flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id={`include-${spec.id}`}
                        checked={!isExcluded}
                        onCheckedChange={(checked) => 
                          handleSpecInclusionToggle(spec.specification_id, !!checked)
                        }
                      />
                      <label 
                        htmlFor={`include-${spec.id}`} 
                        className="cursor-pointer"
                      >
                        {spec.name}
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      {isExcluded ? (
                        <Badge variant="destructive" className="gap-1">
                          <XCircle className="h-3 w-3" />
                          Excluded
                        </Badge>
                      ) : selectedQty ? (
                        <Badge variant="outline">
                          {selectedQty.quantity_option.toLocaleString()} {selectedQty.unit}
                        </Badge>
                      ) : null}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isExcluded ? (
                    <div className="flex items-center gap-2 text-muted-foreground p-4 bg-muted/30 rounded-lg">
                      <XCircle className="h-4 w-4" />
                      <span className="text-sm">This specification will not be included in the award</span>
                    </div>
                  ) : (
                    <RadioGroup
                      value={selections[spec.specification_id] || ''}
                      onValueChange={(value) => handleSelectionChange(spec.specification_id, value)}
                    >
                      <div className="space-y-3">
                        {items.map(item => {
                          const qtyOption = quantityOptions.find(qo => 
                            qo.specification_id === spec.specification_id && qo.quantity_option === item.quantity_option
                          );
                          if (!qtyOption) return null;

                          return (
                            <div key={item.id} className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-muted/50">
                              <RadioGroupItem value={item.id} id={item.id} className="mt-1" />
                              <Label htmlFor={item.id} className="flex-1 cursor-pointer">
                                <div className="grid grid-cols-5 gap-2 text-sm">
                                <div>
                                    <div className="font-medium">
                                      {qtyOption.quantity_option.toLocaleString()} {qtyOption.unit}
                                    </div>
                                    {qtyOption.notes && (
                                      <div className="text-xs text-muted-foreground">{qtyOption.notes}</div>
                                    )}
                                  </div>
                                <div className="text-right">
                                  <div className="text-muted-foreground text-xs">Unit Price</div>
                                  <div className="font-medium">{formatCurrency(item.unit_price_cents)}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-muted-foreground text-xs">Total Price</div>
                                  <div className="font-semibold">{formatCurrency(item.total_price_cents)}</div>
                                </div>
                                <div className="text-right">
                                  <div className="text-muted-foreground text-xs">Est. Tax</div>
                                  <div className="text-muted-foreground">
                                    {item.tax_cents && item.tax_cents > 0 
                                      ? formatCurrency(item.tax_cents)
                                      : '—'}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="text-muted-foreground text-xs">Lead Time</div>
                                  <div>{item.lead_time_days} days</div>
                                </div>
                              </div>
                            </Label>
                          </div>
                          );
                        })}
                      </div>
                    </RadioGroup>
                  )}

                  {/* Editable Distribution Section */}
                  {!isExcluded && selectedQty && needsDistributionConfirmation && (
                    <div className="mt-4 p-4 bg-muted/50 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium">Confirm Distribution Across Locations</div>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => resetDistributionToCalculated(spec.specification_id)}
                          className="h-7 text-xs"
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Reset
                        </Button>
                      </div>
                      
                      <div className="space-y-2">
                        {deliveryLocations.map(loc => {
                          const percentage = distributionMatrix[spec.specification_id]?.[loc.id]?.percentage || 0;
                          const inputValue = inputValues[spec.specification_id]?.[loc.id] ?? 
                            (distributionQuantities[spec.specification_id]?.[loc.id]?.toString() || '0');
                          
                          return (
                            <div key={loc.id} className="flex items-center justify-between gap-4">
                              <div className="flex-1">
                                <span className="text-sm text-muted-foreground">
                                  {loc.delivery_name} ({percentage}%)
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  pattern="[0-9]*"
                                  value={inputValue}
                                  placeholder="0"
                                  onChange={(e) => handleInputChange(
                                    spec.specification_id, 
                                    loc.id, 
                                    e.target.value
                                  )}
                                  onBlur={() => handleInputBlur(spec.specification_id, loc.id)}
                                  className="w-24 h-8 text-right"
                                />
                                <span className="text-sm text-muted-foreground w-12">
                                  {selectedQty.unit}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Distribution Total */}
                      <div className="flex items-center justify-between pt-3 border-t">
                        <span className="text-sm font-medium">Total</span>
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${isValid ? 'text-green-600' : 'text-destructive'}`}>
                            {distributionTotal.toLocaleString()} / {expectedTotal.toLocaleString()} {selectedQty.unit}
                          </span>
                          {isValid ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-destructive" />
                          )}
                        </div>
                      </div>
                      
                      {!isValid && (
                        <p className="text-xs text-destructive">
                          Distribution total must equal the awarded quantity ({expectedTotal.toLocaleString()} {selectedQty.unit})
                        </p>
                      )}
                    </div>
                  )}

                  {/* Read-only Distribution Preview for single location */}
                  {!isExcluded && selectedQty && deliveryLocations.length === 1 && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                      <div className="text-sm font-medium mb-2">Delivery Location:</div>
                      <div className="text-sm">
                        <span className="text-muted-foreground">{deliveryLocations[0].delivery_name}:</span>
                        <span className="font-medium ml-2">
                          {selectedQty.quantity_option.toLocaleString()} {selectedQty.unit}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          <div>
            <Label htmlFor="award-notes">Award Notes (Optional)</Label>
            <Textarea
              id="award-notes"
              placeholder="Add any notes about this award decision"
              value={awardNotes}
              onChange={(e) => setAwardNotes(e.target.value)}
              rows={3}
            />
          </div>

          <div className="flex justify-between items-center p-4 bg-muted/50 rounded-lg">
            <div className="space-y-1">
              <div>
                <div className="text-sm text-muted-foreground">Subtotal</div>
                <div className="text-xl font-semibold">{formatCurrency(calculateTotal())}</div>
              </div>
              {(calculateTotalTax() > 0 || taxLoading) && (
                <div>
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    Est. Tax
                    {taxLoading && <Loader2 className="h-3 w-3 animate-spin" />}
                    {recalculatedTax && !taxLoading && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">Updated</Badge>
                    )}
                  </div>
                  <div className="text-lg">
                    {taxLoading ? (
                      <span className="text-muted-foreground">Calculating...</span>
                    ) : (
                      formatCurrency(calculateTotalTax())
                    )}
                  </div>
                </div>
              )}
              <div>
                <div className="text-sm text-muted-foreground">Total Award Amount</div>
                <div className="text-2xl font-bold">{formatCurrency(calculateTotal() + calculateTotalTax())}</div>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  {includedSpecs.length}/{specifications.length} Specs Included
                </Badge>
                <Badge variant={isComplete() && allDistributionsValid ? 'default' : 'secondary'}>
                  {isComplete() && allDistributionsValid 
                    ? 'Ready to Award' 
                    : `${Object.keys(selections).filter(id => !excludedSpecs.has(id)).length}/${includedSpecs.length} Selected`}
                </Badge>
              </div>
              {includedSpecs.length === 0 && (
                <span className="text-xs text-destructive">At least one specification must be included</span>
              )}
              {includedSpecs.length > 0 && isComplete() && !allDistributionsValid && (
                <span className="text-xs text-destructive">Fix distribution totals</span>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Step 2: Selection Feedback */}
        {currentStep === 'feedback' && (
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-base font-medium">
                  Why did you select this vendor?
                </Label>
                <p className="text-sm text-muted-foreground">
                  Select all that apply. This feedback helps vendors understand their strengths.
                </p>
              </div>
              
              <div className="space-y-2">
                {SELECTION_REASONS.map((reason) => (
                  <div 
                    key={reason.value} 
                    className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                  >
                    <Checkbox
                      id={reason.value}
                      checked={selectedReasons.includes(reason.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedReasons(prev => [...prev, reason.value]);
                        } else {
                          setSelectedReasons(prev => prev.filter(r => r !== reason.value));
                        }
                      }}
                      className="mt-0.5"
                    />
                    <label htmlFor={reason.value} className="cursor-pointer flex-1 space-y-1">
                      <div className="font-medium text-sm">{reason.label}</div>
                      <div className="text-xs text-muted-foreground">{reason.description}</div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="feedback-comments">Additional Comments (Optional)</Label>
              <Textarea
                id="feedback-comments"
                placeholder="Any additional feedback about your vendor selection..."
                value={feedbackComments}
                onChange={(e) => setFeedbackComments(e.target.value.slice(0, FEEDBACK_COMMENTS_MAX_LENGTH))}
                rows={3}
              />
              <p className="text-sm text-muted-foreground text-right">
                {feedbackComments.length}/{FEEDBACK_COMMENTS_MAX_LENGTH} characters
              </p>
            </div>

            {/* Award Summary */}
            <div className="p-4 bg-muted/50 rounded-lg">
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-sm text-muted-foreground">Total Award Amount</div>
                  <div className="text-xl font-bold">{formatCurrency(calculateTotal() + calculateTotalTax())}</div>
                </div>
                <Badge variant="outline">
                  {includedSpecs.length} Spec{includedSpecs.length !== 1 ? 's' : ''} Included
                </Badge>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {currentStep === 'quantities' ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancel
              </Button>
              <Button 
                onClick={() => setCurrentStep('feedback')} 
                disabled={!isComplete() || !allDistributionsValid || loading}
              >
                Next: Selection Feedback
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                onClick={() => setCurrentStep('quantities')} 
                disabled={loading}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
              <Button 
                variant="ghost" 
                onClick={handleAward} 
                disabled={loading}
              >
                Skip
              </Button>
              <Button onClick={handleAward} disabled={loading}>
                {loading ? 'Awarding...' : 'Confirm Award'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
